import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { SajuResult, SajuPillar, Element } from '@/utils/sajuCalculations';
import { ELEMENT_COLORS, ELEMENT_BG_COLORS } from '@/utils/sajuCalculations';
import { getDayPillarInterpretation } from '@/utils/sajuInterpretation';
import { getDayPillarInterpretationEn } from '@/utils/sajuInterpretationEn';
import { calculateDaeun } from '@/utils/daeunCalculations';
import DaeunSection from './DaeunSection';
import SipseongSection from './SipseongSection';
import OhaengSection from './OhaengSection';
import SingangyakSection from './SingangyakSection';
import IljinCalendar from './IljinCalendar';
import ShareCard from './ShareCard';
import SewoonSection from './SewoonSection';
import MonthlyFortuneCards from './MonthlyFortuneCards';
import YearlyFortuneReport from './YearlyFortuneReport';
import AdBanner from './AdBanner';
import type { LunarConversionInfo } from '@/pages/home/page';
import { useSajuHistory } from '@/hooks/useSajuHistory';

interface SajuResultProps {
  result: SajuResult;
  correctedHour: number;
  correctedMinute: number;
  longitude: number;
  eotCorrection: number;
  unknownTime: boolean;
  gender: 'male' | 'female';
  solarYear: number;
  solarMonth: number;
  solarDay: number;
  lunarConvInfo: LunarConversionInfo | null;
  locationName: string;
  onRestart: () => void;
}

interface ExpertSettings {
  useYaJasi: boolean;
  show: boolean;
}

// EN pronunciations
const STEMS_EN = ['Jiǎ','Yǐ','Bǐng','Dīng','Wù','Jǐ','Gēng','Xīn','Rén','Guǐ'];
const BRANCHES_EN = ['Zǐ','Chǒu','Yín','Mǎo','Chén','Sì','Wǔ','Wèi','Shēn','Yǒu','Xū','Hài'];
const PILLAR_LABEL_EN: Record<string, string> = { '년주': 'Year', '월주': 'Month', '일주': 'Day', '시주': 'Hour' };
const ELEMENT_SHORT_EN: Record<Element, string> = { 목: 'Wood', 화: 'Fire', 토: 'Earth', 금: 'Metal', 수: 'Water' };

function PillarCard({ pillar, delay }: { pillar: SajuPillar; delay: number }) {
  const { i18n } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  const isEn = i18n.language === 'en';
  const stemBg = ELEMENT_BG_COLORS[pillar.stemElement];
  const stemColor = ELEMENT_COLORS[pillar.stemElement];
  const branchBg = ELEMENT_BG_COLORS[pillar.branchElement];
  const branchColor = ELEMENT_COLORS[pillar.branchElement];

  const pillarLabel = isEn ? (PILLAR_LABEL_EN[pillar.label] ?? pillar.label) : pillar.label;
  const stemSubLabel = isEn ? STEMS_EN[pillar.stemIdx] : pillar.stemKor;
  const branchSubLabel = isEn ? BRANCHES_EN[pillar.branchIdx] : pillar.branchKor;
  const stemBadgeLabel = isEn ? `Stem · ${ELEMENT_SHORT_EN[pillar.stemElement]}` : `천간·${pillar.stemElement}`;
  const branchBadgeLabel = isEn ? `Branch · ${ELEMENT_SHORT_EN[pillar.branchElement]}` : `지지·${pillar.branchElement}`;

  return (
    <div
      className="flex flex-col items-center gap-1.5 transition-all duration-500"
      style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)' }}
    >
      <p className="text-[11px] text-gray-400 font-medium">{pillarLabel}</p>
      {/* 천간 */}
      <div
        className="w-14 h-14 md:w-20 md:h-20 rounded-xl md:rounded-2xl flex flex-col items-center justify-center gap-0.5 cursor-default"
        style={{ backgroundColor: stemBg }}
      >
        <span className="text-xl md:text-3xl font-bold" style={{ color: stemColor, fontFamily: "'Noto Serif KR', serif" }}>
          {pillar.stem}
        </span>
        <span className={`font-medium ${isEn ? 'text-[9px] leading-tight' : 'text-[10px]'}`} style={{ color: stemColor }}>{stemSubLabel}</span>
      </div>
      {/* 지지 */}
      <div
        className="w-14 h-14 md:w-20 md:h-20 rounded-xl md:rounded-2xl flex flex-col items-center justify-center gap-0.5 cursor-default"
        style={{ backgroundColor: branchBg }}
      >
        <span className="text-xl md:text-3xl font-bold" style={{ color: branchColor, fontFamily: "'Noto Serif KR', serif" }}>
          {pillar.branch}
        </span>
        <span className={`font-medium ${isEn ? 'text-[9px] leading-tight' : 'text-[10px]'}`} style={{ color: branchColor }}>{branchSubLabel}</span>
      </div>
      {/* 오행 태그 */}
      <div className="flex flex-col gap-0.5">
        <ElementBadge element={pillar.stemElement} label={stemBadgeLabel} />
        <ElementBadge element={pillar.branchElement} label={branchBadgeLabel} />
      </div>
    </div>
  );
}

function ElementBadge({ element, label }: { element: Element; label: string }) {
  return (
    <span
      className="text-[9px] md:text-xs px-1.5 py-0.5 rounded-full font-medium text-center whitespace-nowrap"
      style={{ backgroundColor: ELEMENT_BG_COLORS[element], color: ELEMENT_COLORS[element] }}
    >
      {label}
    </span>
  );
}

export default function SajuResultView({
  result,
  correctedHour,
  correctedMinute,
  longitude,
  eotCorrection,
  unknownTime,
  gender,
  solarYear,
  solarMonth,
  solarDay,
  lunarConvInfo,
  locationName,
  onRestart,
}: SajuResultProps) {
  const { t, i18n } = useTranslation();
  const [expert, setExpert] = useState<ExpertSettings>({ useYaJasi: true, show: false });
  const [titleVisible, setTitleVisible] = useState(false);
  const [activeSection, setActiveSection] = useState<'fortune' | 'analysis' | 'calendar'>('fortune');
  const [showYearlyReport, setShowYearlyReport] = useState(false);
  const daeun = calculateDaeun(result, solarYear, solarMonth, solarDay, correctedHour, gender);
  const { addToHistory } = useSajuHistory();

  // 일주 해석 (언어별 분기)
  const dayInterp = i18n.language === 'en'
    ? getDayPillarInterpretationEn(result.dayPillar.stem, result.dayPillar.branch)
    : getDayPillarInterpretation(result.dayPillar.stem, result.dayPillar.branch);

  // 히스토리 저장
  useEffect(() => {
    addToHistory({
      year: solarYear,
      month: solarMonth,
      day: solarDay,
      hour: unknownTime ? null : correctedHour,
      minute: correctedMinute,
      unknownTime,
      locationName,
      saju: result,
    });
  }, []);

  useEffect(() => {
    const t2 = setTimeout(() => setTitleVisible(true), 100);
    return () => clearTimeout(t2);
  }, []);

  const pillars = [
    result.yearPillar,
    result.monthPillar,
    result.dayPillar,
    ...(result.hourPillar && !unknownTime ? [result.hourPillar] : []),
  ];

  const correctedTimeStr = `${String(correctedHour).padStart(2,'0')}:${String(correctedMinute).padStart(2,'0')}`;

  return (
    <div className="min-h-dvh flex flex-col bg-white" style={{ paddingBottom: 'max(5rem, calc(1.5rem + env(safe-area-inset-bottom, 0px)))' }}>
      {/* 헤더 */}
      <div className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-gray-100 px-4 md:px-6 py-3 md:py-4 flex items-center justify-between" style={{ paddingTop: 'max(0.75rem, calc(0.5rem + env(safe-area-inset-top, 0px)))' }}>
        <img
          src="https://public.readdy.ai/ai/img_res/ff2a104a-70f3-4324-bb8d-41c2b9fa3434.png"
          alt="바른사주"
          className="h-7 object-contain"
        />
        <button
          onClick={() => setExpert(e => ({ ...e, show: true }))}
          className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-medium px-3 py-2 rounded-full cursor-pointer whitespace-nowrap transition-all"
        >
          <i className="ri-settings-3-line text-sm" />
          {t('result.expert')}
        </button>
      </div>

      <div className="px-4 md:px-6 pt-6 md:pt-8 pb-4 max-w-lg mx-auto w-full">
        {/* 타이틀 */}
        <div
          className="mb-8 transition-all duration-700"
          style={{ opacity: titleVisible ? 1 : 0, transform: titleVisible ? 'translateY(0)' : 'translateY(16px)' }}
        >
          <div className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 px-3 py-1 rounded-full mb-3 text-xs font-semibold">
            <i className="ri-sparkling-2-fill text-xs" />
            {t('result.badge')}
          </div>
          <h2 className="text-xl md:text-3xl font-bold text-gray-900 leading-snug mb-2">
            {t('result.title').split('\n').map((line, i, arr) => (
              <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
            ))}
          </h2>
          {unknownTime && (
            <div className="bg-amber-50 rounded-xl p-3 border border-amber-100 mt-4">
              <p className="text-amber-700 text-xs">{t('result.unknownTime')}</p>
            </div>
          )}
        </div>

        {/* 음력→양력 변환 알림 */}
        {lunarConvInfo && (
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 mb-6 flex items-start gap-3">
            <div className="w-6 h-6 flex items-center justify-center flex-none mt-0.5">
              <i className="ri-exchange-line text-blue-500 text-base" />
            </div>
            <div>
              <p className="text-xs font-bold text-blue-700 mb-1">{t('result.lunarConv.title')}</p>
              <p className="text-xs text-blue-600 leading-relaxed">
                {lunarConvInfo.calendarType === 'lunarLeap' ? t('result.lunarLeap') : t('result.lunarConv.body1')}
                <strong>{lunarConvInfo.lunarYear}{t('date.yearUnit')} {lunarConvInfo.lunarMonth}{t('date.monthUnit')} {lunarConvInfo.lunarDay}{t('date.dayUnit')}</strong>
                {t('result.lunarConv.body2')}
                <strong>{lunarConvInfo.solarYear}{t('date.yearUnit')} {lunarConvInfo.solarMonth}{t('date.monthUnit')} {lunarConvInfo.solarDay}{t('date.dayUnit')}</strong>
                {t('result.lunarConv.body3')}
              </p>
            </div>
          </div>
        )}

        {/* 4주 8자 */}
        <div className={`flex gap-2 md:gap-5 justify-center mb-6 md:mb-8 ${unknownTime ? 'opacity-90' : ''}`}>
          {pillars.map((p, idx) => (
            <PillarCard key={p.label} pillar={p} delay={300 + idx * 200} />
          ))}
          {unknownTime && (
            <div className="flex flex-col items-center gap-1.5">
              <p className="text-[11px] text-gray-300 font-medium">{i18n.language === 'en' ? 'Hour' : '시주'}</p>
              <div className="w-14 h-14 md:w-20 md:h-20 rounded-xl md:rounded-2xl bg-gray-50 border-2 border-dashed border-gray-200 flex items-center justify-center">
                <i className="ri-question-mark text-gray-300 text-lg md:text-xl" />
              </div>
              <div className="w-14 h-14 md:w-20 md:h-20 rounded-xl md:rounded-2xl bg-gray-50 border-2 border-dashed border-gray-200 flex items-center justify-center">
                <i className="ri-question-mark text-gray-300 text-lg md:text-xl" />
              </div>
              <p className="text-[9px] md:text-xs text-gray-300 text-center">{t('result.timeUnknownHour')}</p>
            </div>
          )}
        </div>

        {/* 계산 근거 공개 */}
        <div className="bg-gray-50 rounded-2xl p-4 mb-6">
          <h4 id="calculation-basis" className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
            <i className="ri-calculator-line text-gray-500" />
            <a href="#calculation-basis">{t('result.calc.title')}</a>
          </h4>
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">{t('result.calc.longitude')}</span>
              <span className="text-gray-900 font-medium">{longitude.toFixed(3)}°E</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">{t('result.calc.eot')}</span>
              <span className="text-gray-900 font-medium">{eotCorrection >= 0 ? '+' : ''}{Math.round(eotCorrection)}min</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">{t('result.calc.trueTime')}</span>
              <span className="text-gray-900 font-bold">{correctedTimeStr}</span>
            </div>
          </div>
        </div>

        {/* 일주 해석 카드 */}
        <div className="bg-white border border-gray-100 rounded-2xl p-5 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-xl">
              <i className="ri-book-open-line text-amber-500 text-sm" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">{dayInterp.title}</h3>
              <p className="text-xs text-gray-400">{result.dayPillar.stem}{result.dayPillar.branch} {t('result.dayInterp.label')}</p>
            </div>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed mb-4">{dayInterp.summary}</p>

          <div className="space-y-3">
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs font-bold text-gray-700 mb-2">{t('result.dayInterp.personality')}</p>
              <div className="flex flex-wrap gap-1.5">
                {dayInterp.personality.map((p, i) => (
                  <span key={i} className="text-xs bg-white px-2.5 py-1 rounded-full text-gray-600 border border-gray-100">{p}</span>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-green-50 rounded-xl p-3">
                <p className="text-xs font-bold text-green-700 mb-1.5">{t('result.dayInterp.strengths')}</p>
                <ul className="space-y-1">
                  {dayInterp.strengths.slice(0,2).map((s, i) => (
                    <li key={i} className="text-xs text-green-600 leading-relaxed">• {s}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-red-50 rounded-xl p-3">
                <p className="text-xs font-bold text-red-700 mb-1.5">{t('result.dayInterp.weaknesses')}</p>
                <ul className="space-y-1">
                  {dayInterp.weaknesses.slice(0,2).map((w, i) => (
                    <li key={i} className="text-xs text-red-600 leading-relaxed">• {w}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-amber-50 rounded-xl p-3">
              <p className="text-xs font-bold text-amber-700 mb-1.5">{t('result.dayInterp.advice')}</p>
              <p className="text-xs text-amber-600 leading-relaxed">{dayInterp.advice}</p>
            </div>
          </div>

          {/* 광고 ① - 일주 해석 카드 내 하단: 콘텐츠 완독 후 자연스러운 노출 */}
          <div className="mt-4 -mx-5 -mb-5 rounded-b-2xl overflow-hidden">
            <AdBanner slot="8162203881" format="auto" className="" />
          </div>
        </div>

        {/* 탭 네비게이션 */}
        <div className="sticky top-[52px] md:top-[57px] z-10 bg-white/95 backdrop-blur-sm border-b border-gray-100 -mx-4 md:-mx-6 px-4 md:px-6 mb-5 md:mb-6">
          <div className="flex gap-0.5">
            {[
              { id: 'fortune' as const, label: t('result.tab.fortune'), icon: 'ri-sparkling-line' },
              { id: 'analysis' as const, label: t('result.tab.analysis'), icon: 'ri-bar-chart-box-line' },
              { id: 'calendar' as const, label: t('result.tab.calendar'), icon: 'ri-calendar-line' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1 md:gap-1.5 py-2.5 md:py-3 text-xs md:text-sm font-semibold transition-all relative whitespace-nowrap cursor-pointer ${
                  activeSection === tab.id ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <i className={`${tab.icon} text-sm md:text-base`} />
                {tab.label}
                {activeSection === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900 rounded-full" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* 탭 컨텐츠 */}
        {activeSection === 'fortune' && (
          <>
            <MonthlyFortuneCards result={result} />
            <DaeunSection daeun={daeun} birthYear={solarYear} />
            {/* 광고 ② - 대운 완독 후 스크롤 멈추는 지점: 자연스러운 콘텐츠 브레이크 */}
            <AdBanner slot="8667367270" format="auto" className="mb-6 rounded-2xl overflow-hidden" />
            <SewoonSection saju={result} />
          </>
        )}

        {activeSection === 'analysis' && (
          <>
            <SipseongSection result={result} />
            <OhaengSection result={result} />
            <SingangyakSection result={result} />
          </>
        )}

        {activeSection === 'calendar' && (
          <IljinCalendar result={result} />
        )}

        {/* 공유 카드 */}
        <ShareCard
          result={result}
          solarYear={solarYear}
          solarMonth={solarMonth}
          solarDay={solarDay}
          unknownTime={unknownTime}
          lunarConvInfo={lunarConvInfo}
        />

        {/* 광고 ③ - 신년운세 버튼 직전: 다음 액션 고민하며 멈추는 고관심 지점 */}
        <AdBanner slot="7354285608" format="auto" className="mb-6 rounded-2xl overflow-hidden" />

        {/* 신년운세보기 버튼 */}
        <button
          onClick={() => setShowYearlyReport(true)}
          className="w-full mb-6 cursor-pointer text-left group"
        >
          <div className="bg-[#0A0A0A] rounded-2xl p-5 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-amber-400 to-transparent" />
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center border border-amber-400/30 rounded-xl flex-none bg-amber-400/5">
                <i className="ri-file-paper-2-line text-amber-400 text-xl" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[10px] text-amber-400/60 font-mono tracking-widest">{t('result.yearlyReport.classified')}</span>
                </div>
                <p className="text-sm font-bold text-white mb-0.5">{t('result.yearlyReport.title')}</p>
                <p className="text-xs text-white/30">{t('result.yearlyReport.subtitle')}</p>
              </div>
              <div className="w-7 h-7 flex items-center justify-center flex-none">
                <i className="ri-arrow-right-line text-amber-400 text-base group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        </button>

        {/* 다시 계산 버튼 */}
        <button
          onClick={onRestart}
          className="w-full bg-gray-900 text-white font-bold text-base py-4 rounded-2xl flex items-center justify-center gap-2 hover:bg-gray-800 active:scale-95 transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line text-sm" />
          {t('result.restart')}
        </button>
      </div>

      {/* 신년운세 리포트 */}
      {showYearlyReport && (
        <YearlyFortuneReport
          result={result}
          solarYear={solarYear}
          solarMonth={solarMonth}
          solarDay={solarDay}
          locationName={locationName}
          correctedHour={correctedHour}
          correctedMinute={correctedMinute}
          gender={gender}
          onClose={() => setShowYearlyReport(false)}
        />
      )}

      {/* 전문가 설정 바텀시트 */}
      {expert.show && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setExpert(e => ({ ...e, show: false }))} />
          <div className="relative bg-white w-full max-w-md rounded-t-3xl p-6" style={{ paddingBottom: 'max(2.5rem, calc(1.5rem + env(safe-area-inset-bottom, 0px)))' }}>
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-6" />
            <div className="flex items-center gap-2 mb-6">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-settings-3-fill text-gray-900 text-lg" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">{t('result.expert.title')}</h3>
            </div>

            <div className="bg-gray-50 rounded-2xl p-4 mb-4">
              <h4 className="text-sm font-bold text-gray-900 mb-1">{t('result.expert.yajasi.title')}</h4>
              <p className="text-xs text-gray-500 leading-relaxed mb-4">{t('result.expert.yajasi.desc')}</p>
              <div className="flex gap-2">
                {[
                  { value: true, label: t('result.expert.yajasi.ya'), desc: t('result.expert.yajasi.ya.desc') },
                  { value: false, label: t('result.expert.yajasi.jo'), desc: t('result.expert.yajasi.jo.desc') },
                ].map(opt => (
                  <button
                    key={String(opt.value)}
                    onClick={() => setExpert(e => ({ ...e, useYaJasi: opt.value }))}
                    className={`flex-1 rounded-xl p-3 text-left transition-all cursor-pointer border-2 ${
                      expert.useYaJasi === opt.value
                        ? 'border-gray-900 bg-gray-900 text-white'
                        : 'border-gray-200 bg-white text-gray-600'
                    }`}
                  >
                    <p className="text-xs font-bold mb-0.5">{opt.label}</p>
                    <p className={`text-xs ${expert.useYaJasi === opt.value ? 'text-white/60' : 'text-gray-400'}`}>{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={() => setExpert(e => ({ ...e, show: false }))}
              className="w-full bg-gray-900 text-white font-bold py-4 rounded-2xl cursor-pointer whitespace-nowrap hover:bg-gray-800 transition-all"
            >
              {t('apply')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
